// src/pages/MemberIdPage.tsx — Slice F, Half B3.
//
// Self-claim screen for the user's NYMR member number. Linked from the
// MePage's heather nudge (visible only when me.member_id_number === null).
//
// Calls PATCH /api/me/member-id. On success: refresh() so MePage sees the
// new value and drops the nudge, then navigate back to /me with a sage
// flash. Same UX pattern as /me/details on save.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, ApiError } from "../api";
import { useCurrentUser } from "../auth";
import { C, F } from "../theme";
import { Button, Card, Eyebrow, Field, TopBar } from "../components/ui";

export function MemberIdPage() {
  const navigate = useNavigate();
  const { me, refresh } = useCurrentUser();

  const [value, setValue] = useState<string>(
    me?.member_id_number !== null && me?.member_id_number !== undefined
      ? String(me.member_id_number)
      : "",
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    const trimmed = value.trim();
    if (trimmed === "") {
      setError("Enter your ID Card Number, or use Cancel to leave it blank.");
      return;
    }
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n <= 0) {
      setError("ID Card Number must be a positive whole number.");
      return;
    }

    setSaving(true);
    try {
      await apiFetch("/api/me/member-id", {
        method: "PATCH",
        body: JSON.stringify({ memberIdNumber: n }),
      });
      await refresh();
      navigate("/me", { state: { flash: "ID Card Number saved." } });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Save failed.");
      setSaving(false);
    }
  }

  return (
    <div>
      <TopBar
        title="ID Card Number"
        sub="Link your NYMR ID Card"
        onBack={() => navigate("/me")}
      />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <Eyebrow>ID Card Number</Eyebrow>
          <p
            style={{
              fontFamily: F.body,
              fontSize: 14,
              color: C.inkSoft,
              fontWeight: 600,
              lineHeight: 1.5,
              margin: "8px 0 14px",
            }}
          >
            We use your ID Card Number to show your comp tickets and visit history. Find it on
            your NYMR ID card, or email{" "}
            <a
              href="mailto:membership@nymr.co.uk"
              style={{ color: C.brassDk, fontWeight: 700, textDecoration: "underline" }}
            >
              membership@nymr.co.uk
            </a>{" "}
            if you're not sure.
          </p>
          <Field
            label="ID Card Number"
            value={value}
            onChange={setValue}
            type="number"
            ph="e.g. 12345"
          />
          {error && (
            <div
              role="status"
              style={{
                padding: 10,
                borderRadius: 10,
                background: C.signalBg,
                border: `1px solid rgba(184,58,43,0.22)`,
                color: C.signalDk,
                fontFamily: F.body,
                fontWeight: 700,
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}
          <Button variant="primary" fullWidth disabled={saving} onClick={save}>
            {saving ? "Saving..." : "Save"}
          </Button>
          <Button
            variant="secondary"
            fullWidth
            disabled={saving}
            style={{ marginTop: 8 }}
            onClick={() => navigate("/me")}
          >
            Cancel
          </Button>
        </Card>
      </div>
    </div>
  );
}
